"use client";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { Rating } from "@components/ui/rating";
import { useLayoutEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@components/ui/avatar";
import { Card, CardContent, CardHeader, CardFooter } from "@components/ui/card";
import { BookOpen, ChartNoAxesColumnIncreasing, Languages, Timer } from "lucide-react";

type CourseCardProps = {
	course: Course;
};

const CourseCard = ({ course }: CourseCardProps) => {
	const router = useRouter();
	const [mounted, setMounted] = useState<boolean>(false);

	const getCleanDescription = (description: string) => {
		if (typeof window !== "undefined") {
			const div = document.createElement("div");
			div.innerHTML = description;
			return div.textContent;
		}
	};

	useLayoutEffect(() => setMounted(true), []);

	if (mounted) {
		return (
			<Card className="w-full cursor-pointer gap-0 overflow-hidden border-0 p-0 shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl" onClick={() => router.push(`/store/item/${course?.uuid}`)}>
				<CardHeader className="relative">
					<Image alt={course?.name} className="h-40 w-full object-cover sm:h-48" src={course?.thumbnailUrl} width={400} height={200} />
				</CardHeader>
				<CardContent className="px-5 py-2">
					<div className="flex w-full flex-col justify-center gap-1">
						<h1 className="my-auto line-clamp-2 font-semibold">{course?.name}</h1>
						<Rating className="w-fit" max={parseFloat(course?.rating?.split("/").at(1) ?? "5.0")} readonly size={12} value={parseFloat(course?.rating?.split("/").at(0) ?? "0.0")} variant="warning" />
					</div>
					<p className="text-muted-foreground line-clamp-3 text-sm">{getCleanDescription(course?.description)}</p>
					<div className="grid grid-cols-2 gap-2">
						<div className="flex items-center gap-1">
							<div className="bg-secondary/20 rounded-full p-1.5">
								<BookOpen className="stroke-secondary" size={16} />
							</div>
							<span className="text-muted-foreground text-sm font-medium">{`${course?.noOfLessons} Lessons`}</span>
						</div>
						<div className="flex items-center gap-1">
							<div className="bg-secondary/20 rounded-full p-1.5">
								<Timer className="stroke-secondary" size={16} />
							</div>
							<span className="text-muted-foreground text-sm font-medium">{course?.duration}</span>
						</div>
						<div className="flex items-center gap-1">
							<div className="bg-secondary/20 rounded-full p-1.5">
								<Languages className="stroke-secondary" size={16} />
							</div>
							<span className="text-muted-foreground text-sm font-medium capitalize">{course?.language?.toLowerCase()}</span>
						</div>
						<div className="flex items-center gap-1">
							<div className="bg-secondary/20 rounded-full p-1.5">
								<ChartNoAxesColumnIncreasing className="stroke-secondary" size={16} />
							</div>
							<span className="text-muted-foreground text-sm font-medium capitalize">{course?.difficulty?.toLowerCase()}</span>
						</div>
					</div>
				</CardContent>
				<CardFooter className="flex items-center justify-between px-5 py-2">
					<div className="flex items-center gap-2">
						<Avatar className="border-secondary border-2 p-1">
							<AvatarImage alt={course?.offeredBy === "ORGANIZATION" ? course?.creator?.name : course?.creator?.firstName} src={course?.offeredBy === "ORGANIZATION" ? course?.creator?.logoUrl : course?.creator?.user?.avatarUrl} />
							<AvatarFallback className="bg-accent text-accent-foreground text-xs">{course?.offeredBy === "ORGANIZATION" ? course?.creator?.name?.at(0)?.toUpperCase() : course?.creator?.firstName?.at(0)?.toUpperCase()}</AvatarFallback>
						</Avatar>
						<div className="flex w-full flex-col justify-center">
							<span className="text-muted-foreground text-xs">{"By"}</span>
							<span className="text-muted-foreground line-clamp-1 text-sm font-medium">
								{course?.offeredBy === "ORGANIZATION"
									? course?.creator?.name
									: course?.creator?.middleName
										? `${course?.creator?.title} ${course?.creator?.firstName} ${course?.creator?.middleName} ${course?.creator?.lastName}`
										: `${course?.creator?.title} ${course?.creator?.firstName} ${course?.creator?.lastName}`}
							</span>
						</div>
					</div>
					<span className="text-secondary font-bold">{course?.type === "FREE" ? "Free" : `${course?.currency}${course?.price}`}</span>
				</CardFooter>
			</Card>
		);
	}
};

export default CourseCard;
